import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { formatCurrency } from '../../../utils/formatCurrency.js';
import { buildDashboardChartData } from '../../../utils/dashboardChartData.js';
import { DateCategoryTick, useChartBreakpoint } from './chartAxis.js';
import {
  CHART_DUE_COLOR,
  CHART_PAID_COLOR,
  CHART_TOOLTIP_WRAPPER_PROPS,
  DashboardAmountTooltip,
  formatChartValue,
} from './chartTheme.js';

const DEFAULT_LINES = [
  { dataKey: 'paid', nameKey: 'dashboard.paidAmount', color: CHART_PAID_COLOR },
  { dataKey: 'balance', nameKey: 'dashboard.balanceAmount', color: CHART_DUE_COLOR },
];

const DashboardLineChart = ({
  title,
  chartTrend,
  lines = DEFAULT_LINES,
  format = 'currency',
  emptyLabel,
  t,
}) => {
  const breakpoint = useChartBreakpoint();
  const data = buildDashboardChartData(chartTrend);
  const resolvedLines = lines.filter((line) => line.dataKey);

  const tickFormatter = (value) => {
    if (format === 'currency') return formatCurrency(value);
    return formatChartValue(value, format);
  };

  return (
    <div>
      {title ? <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p> : null}
      {data.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">{emptyLabel || t?.('common.noData') || 'No data'}</p>
      ) : (
        <div className="h-[200px] md:h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: breakpoint === 'sm' ? 8 : 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                interval="preserveStartEnd"
                minTickGap={breakpoint === 'sm' ? 16 : 24}
                tick={<DateCategoryTick />}
                height={28}
              />
              <YAxis
                width={breakpoint === 'sm' ? 44 : 56}
                tick={{ fontSize: breakpoint === 'sm' ? 10 : 12 }}
                tickFormatter={tickFormatter}
              />
              <Tooltip
                {...CHART_TOOLTIP_WRAPPER_PROPS}
                content={<DashboardAmountTooltip format={format} />}
              />
              <Legend wrapperStyle={{ fontSize: breakpoint === 'sm' ? 11 : 13 }} />
              {resolvedLines.map((line) => (
                <Line
                  key={line.dataKey}
                  type="monotone"
                  dataKey={line.dataKey}
                  name={line.name || (line.nameKey && t ? t(line.nameKey) : line.dataKey)}
                  stroke={line.color}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default DashboardLineChart;
